import React, { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { ClipboardList, Factory, ScanLine, ShieldAlert } from 'lucide-react'

interface KpiData {
  openTasks: number
  activeMachines: number
  inspectionsToday: number
  openProblems: number
}

export const KpiBar: React.FC = () => {
  const [data, setData] = useState<KpiData>({ openTasks: 0, activeMachines: 0, inspectionsToday: 0, openProblems: 0 })
  const [loading, setLoading] = useState(true)

  const load = async () => {
    try {
      const today = new Date().toISOString().split('T')[0]
      const [tasks, machines, inspections, problems] = await Promise.all([
        supabase.from('tasks').select('id').eq('completed', false),
        supabase.from('machines').select('id').eq('status', 'active'),
        supabase.from('quality_control').select('id').gte('created_at', today + 'T00:00:00Z'),
        supabase.from('probleem_meldingen').select('id').eq('oplossing_gevonden', false),
      ])
      setData({
        openTasks: tasks.data?.length ?? 0,
        activeMachines: machines.data?.length ?? 0,
        inspectionsToday: inspections.data?.length ?? 0,
        openProblems: problems.data?.length ?? 0,
      })
    } catch {
      // KPI bar is informational, silently fail
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    const iv = setInterval(load, 60000)
    return () => clearInterval(iv)
  }, [])

  const tiles = [
    { label: 'Open taken', value: data.openTasks, icon: ClipboardList, tone: 'amber', helper: 'Nog af te ronden' },
    { label: 'Actieve machines', value: data.activeMachines, icon: Factory, tone: 'blue', helper: 'Momenteel in bedrijf' },
    { label: 'Inspecties vandaag', value: data.inspectionsToday, icon: ScanLine, tone: 'green', helper: 'Vandaag geregistreerd' },
    { label: 'Open problemen', value: data.openProblems, icon: ShieldAlert, tone: 'red', helper: 'Vereist aandacht' },
  ]

  return (
    <div className="w-full grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
      {tiles.map(t => {
        const Icon = t.icon
        return (
          <div key={t.label} className="metric-card">
            <div className={`metric-icon metric-icon-${t.tone}`}><Icon size={18} /></div>
            <div className="min-w-0">
              <div className="text-[11px] sm:text-xs font-medium truncate" style={{ color: 'var(--text-secondary)' }}>{t.label}</div>
              <div className="text-2xl sm:text-[28px] leading-tight font-semibold tracking-[-0.03em] mt-1" style={{ color: 'var(--text-primary)' }}>
                {loading ? '–' : t.value}
              </div>
              <div className="hidden sm:block text-[10px] mt-1 truncate" style={{ color: 'var(--text-tertiary)' }}>{t.helper}</div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
